<template>
  <div class="rb-ob-form rb-match-page">
    <header>
      <h1 class="rb-ob-title">{{ $t('intake.rematchOfferTitle') }}</h1>
    </header>

    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <p v-else-if="loading" class="rb-ob-status" role="status">{{ $t('intake.portal.loading') }}</p>
    <p v-else-if="!loading && !therapist" class="rb-ob-status" role="status">{{ $t('intake.portal.noData') }}</p>
    <p v-if="!loading && !error && !rematch" class="rb-ob-status" role="status">{{ $t('intake.rematchNoneActive') }}</p>

    <div class="rb-ob-status-panel rb-match-rematch-panel" v-if="therapist && rematch">
      <div class="rb-match-outcome-therapist">
        <span class="rb-match-therapist-avatar" aria-hidden="true">{{ therapist.initial }}</span>
        <p class="rb-ob-q">
          {{ $t('intake.rematchCurrentTherapist', {name: therapistName}) }}
        </p>
      </div>

      <ul class="rb-match-therapist-facts" :aria-label="$t('intake.sessionFactsAria')">
        <li v-if="therapist.price != null">
          <span class="rb-match-fact-label">{{ $t('intake.sessionOnlineHour') }}</span>
          <span class="rb-match-fact-value">
            {{ formatMoneyAmount(therapist.price, therapist.currency, lang) }}
          </span>
        </li>
        <li v-if="therapist.title">
          <span class="rb-match-fact-label">{{ $t('intake.verifiedTherapist') }}</span>
          <span class="rb-match-fact-value">{{ therapist.title }}</span>
        </li>
      </ul>

      <p class="rb-match-rematch-reason">{{ $t('intake.rematchOfferReason') }}</p>

      <ul class="rb-match-outcome-bullets">
        <li>{{ $t('intake.rematchBulletReject') }}</li>
        <li>{{ $t('intake.rematchBulletUnchanged') }}</li>
      </ul>
    </div>

    <div class="rb-ob-actions">
      <button class="rb-ob-submit" type="button" :disabled="loading || submitting || !canDecide" @click="acceptRematch">
        {{ $t('intake.rematchAcceptAction') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" :disabled="loading || submitting || !canDecide" @click="declineRematch">
        {{ $t('intake.rematchDeclineAction') }}
      </button>
      <p v-if="error" class="rb-ob-err is-visible" role="alert">{{ error }}</p>
      <button v-if="error && !rematch" class="rb-ob-submit rb-ob-submit-secondary" type="button" :disabled="loading" @click="bootstrap">{{ $t('intake.rematchRetryAction') }}</button>
      <router-link class="rb-ob-submit rb-ob-submit-secondary" :to="{name: 'portalHome', params: {lang}}">{{ $t('intake.rematchBackToCareAction') }}</router-link>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchProfile,
  getCachedProfile,
  hasAuthToken,
  resolveDoctor,
  therapistUiFromDoctor,
  formatMoneyAmount,
  fetchActiveClinicalResponse,
  decideClinicalRematch,
} from '@/intake/portalApi'

export default {
  name: 'RematchOfferStep',
  data() {
    return {
      loading: true,
      needsLogin: false,
      therapist: null,
      rematch: null,
      submitting: false,
      error: '',
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    canDecide() { return !this.needsLogin && !!(this.rematch && this.rematch.id && this.rematch.status === 'OFFERED') },
    therapistName() {
      if (!this.therapist || !this.therapist.name) return ''
      return String(this.therapist.name).replace(/^دکتر\s+/, '').replace(/^Dr\.\s+/i, '')
    },
  },
  mounted() {
    this.bootstrap()
  },
  methods: {
    formatMoneyAmount,
    bootstrap() {
      this.loading = true
      this.error = ''
      this.rematch = null
      this.needsLogin = !hasAuthToken()
      const apply = (profile) => {
        this.therapist = therapistUiFromDoctor(resolveDoctor(profile), this.lang)
      }
      if (!hasAuthToken()) {
        apply(getCachedProfile())
        this.loading = false
        return
      }
      return Promise.all([
        fetchProfile().catch(() => getCachedProfile()),
        fetchActiveClinicalResponse(),
      ]).then(([profile, clinical]) => {
        apply(profile)
        this.rematch = clinical && clinical.rematch ? clinical.rematch : null
        if (!this.rematch) return
        if (!this.rematch.id) throw new Error('REMATCH_STATUS_UNAVAILABLE')
        if (this.rematch.status && this.rematch.status !== 'OFFERED') this.$router.replace({name: 'intakeRematchRun', params: {lang: this.lang}})
      }).catch(() => { this.error = this.$t('intake.rematchStatusUnavailable') }).finally(() => {
        this.loading = false
      })
    },
    acceptRematch() {
      if (this.loading || this.submitting || !this.canDecide) return
      this.$router.push({
        name: 'intakeRematchDecide',
        params: {lang: this.lang},
      })
    },
    declineRematch() {
      if (this.loading || !this.canDecide || this.submitting) return
      this.submitting = true
      this.error = ''
      return decideClinicalRematch(this.rematch.id, 'decline')
          .then((result) => {
            const confirmed = result && (result.rematch || result)
            if (!confirmed || String(confirmed.id) !== String(this.rematch.id) || confirmed.status !== 'DECLINED') throw new Error('REMATCH_DECISION_UNCONFIRMED')
            this.$router.push({name: 'portalHome', params: {lang: this.lang}})
          })
          .catch(() => { this.error = this.$t('intake.rematchDecisionFailed') })
          .finally(() => { this.submitting = false })
    },
  },
}
</script>
